'use client';

import { Transition, TransitionChild } from '@headlessui/react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { useMemo, useRef } from 'react';

import { cx } from '../cx';
import { ModalContextActions, ModalContextState } from './context';

export type ModalProps = DialogPrimitive.DialogProps &
    Omit<DialogPrimitive.DialogContentProps, 'forceMount'>;

export function Modal({
    children,
    className,
    open: openProp,
    defaultOpen,
    modal,
    onOpenChange,
    ...props
}: ModalProps) {
    const [open, setOpen] = useControllableState({
        prop: openProp,
        defaultProp: defaultOpen,
        onChange: onOpenChange,
    });

    const state = useMemo(
        () => ({
            open: open || false,
        }),
        [open]
    );

    const initialOpen = useRef(state.open);

    const actions = useMemo(
        () => ({
            open: () => setOpen(true),
            close: () => setOpen(false),
        }),
        [setOpen]
    );

    return (
        <DialogPrimitive.Root
            modal={modal}
            open={open}
            defaultOpen={defaultOpen}
            onOpenChange={setOpen}
        >
            <DialogPrimitive.Portal forceMount>
                <Transition show={state.open} appear={initialOpen.current}>
                    <div className="fixed inset-0 z-50 flex min-h-0 flex-col items-center justify-center">
                        <TransitionChild>
                            <DialogPrimitive.Overlay
                                forceMount
                                className={cx(
                                    'absolute inset-0 bg-black/50',
                                    'backdrop-blur transition',
                                    'data-[closed]:opacity-0 data-[closed]:backdrop-blur-0'
                                )}
                            />
                        </TransitionChild>
                        <div className="sm:max-w-container sm:p-sm md:p-md lg:p-lg relative flex h-full min-h-0 w-full flex-col items-center sm:h-auto">
                            <TransitionChild>
                                <DialogPrimitive.Content
                                    {...props}
                                    forceMount
                                    className={cx(
                                        'min-0 flex w-full flex-auto translate-x-0 flex-col justify-center overflow-hidden rounded-none bg-white shadow-2xl sm:rounded-xl',
                                        className,
                                        'transition-all',
                                        'data-[closed]:scale-90 data-[closed]:opacity-0',
                                        'data-[enter]:delay-100'
                                    )}
                                >
                                    <ModalContextActions.Provider
                                        value={actions}
                                    >
                                        <ModalContextState.Provider
                                            value={state}
                                        >
                                            {children}
                                        </ModalContextState.Provider>
                                    </ModalContextActions.Provider>
                                </DialogPrimitive.Content>
                            </TransitionChild>
                        </div>
                    </div>
                </Transition>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
